/**
 * Shared helpers for account-matching / primary-login-selection flows.
 * Used by Verification and SelectPrimaryLogin containers.
 */

import { WORKFLOW_TYPE_LINK_ACCOUNT } from 'helpers/registrationWorkflow';

export const normalizeEmail = (v) => String(v || '').trim().toLowerCase();
export const normalizeContactValue = (v) =>
  String(v || '').trim().toLowerCase();

export const normalizePhoneForCompare = (v) =>
  String(v || '').replace(/\D/g, '');

/**
 * True when getAccountByPhoneNumber found a verified phone on an account.
 */
export const isPhoneOnExistingAccount = (accountLookup) =>
  accountLookup?.exists === true;

export const isLinkAccountWorkflow = (workflowType) =>
  workflowType === WORKFLOW_TYPE_LINK_ACCOUNT;

/**
 * Link flow where a confirmed (non-provisioned) account was found — formerly MATCHCONFIRMED.
 */
export const isMatchConfirmedLinkFlow = (
  workflowType,
  isConfirmed,
  isProvisioned,
) =>
  isLinkAccountWorkflow(workflowType) && !!isConfirmed && !isProvisioned;

/**
 * PROVISIONED and match-confirmed link flows must not continue when the phone
 * is verified on another account.
 */
export const isLinkingFlowBlockedForPhoneOnAnotherAccount = ({
  isProvisioned,
  isConfirmed,
  workflowType,
} = {}) =>
  !!isProvisioned ||
  isMatchConfirmedLinkFlow(workflowType, isConfirmed, isProvisioned);

/**
 * PROVISIONED and match-confirmed link flows: block before phone OTP when
 * getAccountByPhoneNumber finds the number on an existing account.
 */
export const shouldBlockLinkingPhoneBeforeVerify = (
  accountLookup,
  { isProvisioned, isConfirmed, workflowType } = {},
) => {
  if (
    !isLinkingFlowBlockedForPhoneOnAnotherAccount({
      isProvisioned,
      isConfirmed,
      workflowType,
    })
  ) {
    return false;
  }
  return isPhoneOnExistingAccount(accountLookup);
};

/**
 * After phone OTP verify: stop provisioned / match-confirmed link flows when
 * the phone belongs to another account (SelectPrimaryLogin must not show).
 */
export const shouldStopLinkingForPhoneOnAnotherAccount = ({
  isPhoneAccountMatch,
  isProvisioned,
  isConfirmed,
  workflowType,
} = {}) =>
  !!isPhoneAccountMatch &&
  isLinkingFlowBlockedForPhoneOnAnotherAccount({
    isProvisioned,
    isConfirmed,
    workflowType,
  });

/**
 * Converts a telecom list (email or phone) from the account API into
 * ranked options suitable for display in the primary-login selection UI.
 * Only verified items with rank 1 or 2 are kept.
 */
export const mapTelecomToRankedOptions = (list) =>
  Array.from(
    (list || [])
      .filter((t) => t.verified !== false)
      .map((t) => ({
        value: t.value,
        rank: t.rank ?? (t.isAlias ? 1 : 2),
      }))
      .filter((t) => t.rank === 1 || t.rank === 2)
      .reduce((acc, item) => {
        const key = normalizeContactValue(item.value);
        if (!key) return acc;
        const existing = acc.get(key);
        if (!existing || item.rank < existing.rank) {
          acc.set(key, item);
        }
        return acc;
      }, new Map())
      .values(),
  );

/**
 * Ensures the email the user originally entered during registration is
 * present in the option list (appended as rank 2 if missing), then sorts
 * by rank ascending and value alphabetically.
 */
export const mergeEnteredEmailIntoOptions = (emailOptions, enteredEmail) => {
  if (!enteredEmail) return emailOptions;
  const exists = emailOptions.some(
    (e) => normalizeEmail(e.value) === normalizeEmail(enteredEmail),
  );
  const merged = exists
    ? emailOptions
    : [...emailOptions, { value: enteredEmail, rank: 2 }];
  return merged.sort(
    (a, b) =>
      a.rank - b.rank || String(a.value).localeCompare(String(b.value)),
  );
};

/**
 * Moves the registration email to the front of the list so the user sees
 * the address they started with first.
 */
export const putRegistrationEmailFirst = (list, registrationEmail) => {
  if (!registrationEmail || !list?.length) return list;
  const norm = normalizeEmail(registrationEmail);
  const idx = list.findIndex((e) => normalizeEmail(e.value) === norm);
  if (idx <= 0) return list;
  const next = [...list];
  const [item] = next.splice(idx, 1);
  return [item, ...next];
};

/**
 * Ensures the phone the user entered during registration is present in the
 * option list (appended as rank 2 if missing), then sorts by rank.
 */
export const mergeEnteredPhoneIntoOptions = (phoneOptions, enteredPhone) => {
  if (!enteredPhone) return phoneOptions;
  const enteredNorm = normalizePhoneForCompare(enteredPhone);
  const exists = phoneOptions.some(
    (p) => normalizePhoneForCompare(p.value) === enteredNorm,
  );
  const merged = exists
    ? phoneOptions
    : [...phoneOptions, { value: enteredPhone, rank: 2 }];
  return merged.sort(
    (a, b) =>
      a.rank - b.rank || String(a.value).localeCompare(String(b.value)),
  );
};

/**
 * Moves the registration phone to the front of the list.
 */
export const putRegistrationPhoneFirst = (list, registrationPhone) => {
  if (!registrationPhone || !list?.length) return list;
  const norm = normalizePhoneForCompare(registrationPhone);
  const idx = list.findIndex(
    (p) => normalizePhoneForCompare(p.value) === norm,
  );
  if (idx <= 0) return list;
  const next = [...list];
  const [item] = next.splice(idx, 1);
  return [item, ...next];
};

/**
 * Extracts hstreamId from a verify-OTP response, handling casing variants.
 * Returns trimmed string or undefined.
 */
export const extractHstreamIdFromResponse = (verifyResponse) => {
  const v = verifyResponse?.hstreamId ?? verifyResponse?.HstreamId;
  if (v == null || String(v).trim() === '') return undefined;
  return String(v).trim();
};

/**
 * Reads the emails and phone arrays from an account-info response,
 * tolerating both camelCase and PascalCase property names.
 */
export const extractVerifiedContacts = (accountData) => ({
  emails: accountData?.emails ?? accountData?.Emails ?? [],
  phones: accountData?.phoneNumbers ?? accountData?.PhoneNumbers ?? [],
});
